import React from "react";
import {
  DailyChargeGroup,
  extractChargeItemsFromPayload,
  extractCurrencyFromPayload,
  getChargeItemAmount,
  getChargeItemLabel,
  groupChargeItemsByDay,
} from "../../../utils/warehouseReservationCharges";

type ChargesLocale = "en" | "fr";

const CHARGE_LABELS: Record<
  ChargesLocale,
  {
    title: string;
    loading: string;
    noCharges: string;
    accommodation: string;
    options: string;
    noAccommodation: string;
    noOptions: string;
    optionsSuffix: string;
  }
> = {
  en: {
    title: "Daily charges",
    loading: "Loading charges...",
    noCharges: "No accommodation or option charges in payload.",
    accommodation: "Accommodation",
    options: "Options",
    noAccommodation: "No accommodation",
    noOptions: "No options",
    optionsSuffix: "options",
  },
  fr: {
    title: "Charges journalières",
    loading: "Chargement des charges...",
    noCharges: "Aucune charge hébergement ou option dans le payload.",
    accommodation: "Hébergement",
    options: "Options",
    noAccommodation: "Aucun hébergement",
    noOptions: "Aucune option",
    optionsSuffix: "options",
  },
};

interface WarehouseReservationDailyChargesProps {
  payload: unknown;
  loading?: boolean;
  locale?: ChargesLocale;
}

function formatMoney(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
}

function ChargeList({
  items,
  currency,
  emptyLabel,
}: {
  items: DailyChargeGroup["accommodation"];
  currency: string;
  emptyLabel: string;
}) {
  if (!items.length) {
    return <div className="text-xs text-gray-400 italic">{emptyLabel}</div>;
  }

  return (
    <ul className="space-y-2">
      {items.map((item, index) => (
        <li
          key={item.id ?? `${getChargeItemLabel(item)}-${index}`}
          className="flex items-start justify-between gap-3 text-xs"
        >
          <span className="text-gray-700">{getChargeItemLabel(item)}</span>
          <span className="font-medium text-gray-900 shrink-0">
            {formatMoney(getChargeItemAmount(item), item.currency || currency)}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function WarehouseReservationDailyCharges({
  payload,
  loading = false,
  locale = "en",
}: WarehouseReservationDailyChargesProps) {
  const labels = CHARGE_LABELS[locale];
  const chargeItems = extractChargeItemsFromPayload(payload);
  const currency = extractCurrencyFromPayload(payload);
  const dailyGroups = groupChargeItemsByDay(chargeItems);

  if (loading) {
    return (
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="text-sm font-semibold text-gray-900 mb-2">{labels.title}</div>
        <div className="text-xs text-gray-500">{labels.loading}</div>
      </div>
    );
  }

  if (!dailyGroups.length) {
    return (
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="text-sm font-semibold text-gray-900 mb-2">{labels.title}</div>
        <div className="text-xs text-gray-500">{labels.noCharges}</div>
      </div>
    );
  }

  const totalAccommodation = dailyGroups.reduce((sum, group) => sum + group.accommodationTotal, 0);
  const totalOptions = dailyGroups.reduce((sum, group) => sum + group.optionsTotal, 0);

  return (
    <div className="px-4 py-3 border-b border-gray-200">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h4 className="text-sm font-semibold text-gray-900">{labels.title}</h4>
        <div className="text-xs text-gray-500">
          {formatMoney(totalAccommodation, currency)} · {formatMoney(totalOptions, currency)}{" "}
          {labels.optionsSuffix}
        </div>
      </div>

      <div className="space-y-3">
        {dailyGroups.map(group => (
          <div
            key={group.date ?? "undated"}
            className="rounded-md border border-gray-200 bg-white overflow-hidden"
          >
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-800">{group.dateLabel}</span>
              <span className="text-xs text-gray-500">
                {formatMoney(group.accommodationTotal + group.optionsTotal, currency)}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-gray-200">
              <div className="p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700 mb-2">
                  {labels.accommodation}
                </div>
                <ChargeList
                  items={group.accommodation}
                  currency={currency}
                  emptyLabel={labels.noAccommodation}
                />
              </div>
              <div className="p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 mb-2">
                  {labels.options}
                </div>
                <ChargeList
                  items={group.options}
                  currency={currency}
                  emptyLabel={labels.noOptions}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
